/**
 * Decision History Process
 * 
 * Simple view that displays decision history as a list.
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import { 
  designTokens, 
  createHeading, 
  createListItem,
  createScrollableContainer,
  createEmptyState,
  createContainer,
  createButton
} from '../../core/design-system.js';

interface Decision {
  id: string;
  query: string;
  timestamp: number;
  selectedOption?: string;
  confidence?: number;
}

export class DecisionHistoryProcess extends BaseProcess {
  kind: string = 'decision-history';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'productivity' as const,
    canStop: true,
    preserveState: true,
    timeSensitive: false
  };

  private decisions: Decision[] = [];

  private historyView: DecisionHistoryView | null = null;

  constructor(id: string) {
    super(id);
    this.loadDecisions();
  }

  private loadDecisions(): void {
    // TODO: Load from API/localStorage
    // For now, use empty array
    this.decisions = [];
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[DecisionHistoryProcess] onStart called for process ${this.id}`);
    
    // Listen for decision-history.show event
    bus.on('decision-history.show', () => {
      this.showDecisionHistory(ctx);
    });
    
    // Check URL for /history route
    if (window.location.pathname.includes('/history')) {
      this.showDecisionHistory(ctx);
    }
    
    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  private showDecisionHistory(ctx: ProcessContext): void {
    if (this.historyView) {
      // Already showing
      return;
    }
    
    const view = new DecisionHistoryView(
      this.id,
      `${this.id}-main`,
      this.decisions,
      this
    );
    const viewInstance = view.toViewInstance();
    this.historyView = view;
    
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);
  }

  addDecision(decision: Decision): void {
    this.decisions.unshift(decision); // Add to beginning
    if (this.historyView) {
      this.historyView.updateDecisions(this.decisions);
    }
  }

  updateDecisions(decisions: Decision[]): void {
    this.decisions = decisions;
    if (this.historyView) {
      this.historyView.updateDecisions(this.decisions);
    }
  }
}

/**
 * Decision History View
 * 
 * Renders decisions as a simple list.
 */
class DecisionHistoryView extends BaseViewHTML {
  constructor(
    processId: string,
    viewId: string,
    private decisions: Decision[],
    private process: DecisionHistoryProcess
  ) {
    super(processId, viewId, {
      sizes: [1, 3], // Full height, normal width (default); full height, double width (alternative)
      showDismissButton: true,
      resizable: true,
    });
  }

  protected getContentHTML(): string {
    const heading = createHeading(2, 'Decision History');
    
    const decisionsHTML = this.decisions.length === 0
      ? createEmptyState('No decisions yet')
      : this.decisions.map(decision => {
          const date = new Date(decision.timestamp);
          const dateStr = date.toLocaleDateString();
          const timeStr = date.toLocaleTimeString();
          
          const content = `
            <div style="
              display: flex;
              flex-direction: column;
              gap: ${designTokens.padding.xs};
            ">
              <div style="
                font-size: ${designTokens.fontSize.sm};
                color: ${designTokens.contentColor};
                font-weight: 500;
              ">${this.escapeHtml(decision.query)}</div>
              ${decision.selectedOption ? `
                <div style="
                  font-size: ${designTokens.fontSize.xs};
                  color: ${designTokens.contentColorMuted};
                ">Selected: ${this.escapeHtml(decision.selectedOption)}</div>
              ` : ''}
              <div style="
                font-size: ${designTokens.fontSize.xs};
                color: ${designTokens.contentColorMuted};
              ">${dateStr} at ${timeStr}</div>
            </div>
          `;
          
          return createListItem(content, {
            className: 'decision-item',
            style: `cursor: pointer;`
          });
        }).join('');
    
    const scrollableContent = createScrollableContainer(
      `<ul style="list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: ${designTokens.padding.sm};">${decisionsHTML}</ul>`,
      { direction: 'vertical' }
    );
    
    return createContainer(
      heading + scrollableContent,
      { padding: 'lg', flexDirection: 'column' }
    );
  }

  protected render(): void {
    super.render();
    
    if (!this.element) return;
    
    // Setup click handlers for decision items
    const decisionItems = this.element.querySelectorAll('.decision-item');
    decisionItems.forEach((item, index) => {
      item.addEventListener('click', () => {
        const decision = this.decisions[index];
        if (decision) {
          bus.emit('decision.view', { decisionId: decision.id });
        }
      });
    });
  }

  updateDecisions(newDecisions: Decision[]): void {
    this.decisions = newDecisions;
    this.markDirty();
  }
}

registerProcess('decision-history', (id: string) => new DecisionHistoryProcess(id));

